import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getTenantForEmail, getTenantForHostname } from '../config/tenants';
import { createUserPool, createCognitoUser, createAuthDetails } from '../config/cognito';
import { mapCognitoError } from '../utils/cognitoErrors';

const AuthContext = createContext(null);

/**
 * Auth challenges returned by Cognito during sign-in.
 */
export const AUTH_CHALLENGE = {
  NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED',
  PASSWORD_RESET_REQUIRED: 'PASSWORD_RESET_REQUIRED',
};

/**
 * AuthProvider — wraps the app and manages Cognito session state.
 *
 * Token strategy (ADR-008):
 *   - Access token: stored in React state (JS memory only — never written to storage)
 *   - Refresh token: managed by the Cognito SDK session (sessionStorage-scoped for pilot;
 *     httpOnly cookie approach requires a backend and is deferred to Phase 2)
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authChallenge, setAuthChallenge] = useState(null);
  const [error, setError] = useState(null);

  // Refs to hold Cognito objects across challenge steps
  const cognitoUserRef = useRef(null);
  const userPoolRef = useRef(null);

  /**
   * Hydrate auth state from a valid Cognito session.
   */
  const hydrateSession = useCallback((cognitoUser, session) => {
    const idToken = session.getIdToken();

    // Read attributes from the ID token payload directly
    const payload = idToken.decodePayload();
    const userAttributes = {
      email: payload.email || '',
      name: payload.name || payload.email || '',
      'custom:role': payload['custom:role'] || 'manager',
      'custom:tenant_id': payload['custom:tenant_id'] || '',
      sub: payload.sub || '',
    };

    setUser({ username: cognitoUser.getUsername(), attributes: userAttributes });
    setAccessToken(session.getAccessToken().getJwtToken());
    setIsAuthenticated(true);
  }, []);

  /**
   * On mount — check if there's an existing valid Cognito session.
   * Restores auth state across page refreshes.
   */
  useEffect(() => {
    const restoreSession = async () => {
      // E2E test bypass: Playwright sets window.__E2E_MOCK_AUTH__ via addInitScript
      // Optional: set window.__E2E_MOCK_ROLE__ to override role (default: 'manager')
      if (typeof window !== 'undefined' && window.__E2E_MOCK_AUTH__) {
        const mockRole = (typeof window.__E2E_MOCK_ROLE__ === 'string' && window.__E2E_MOCK_ROLE__) || 'manager';
        setUser({
          username: 'e2e-test@nexonurse.com',
          attributes: {
            email: 'e2e-test@nexonurse.com',
            name: 'E2E Test User',
            'custom:role': mockRole,
            'custom:tenant_id': 'nexonurse',
            sub: 'e2e-mock-sub-001',
          },
        });
        setAccessToken('e2e-mock-token');
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      try {
        // Try to resolve a user pool from the current hostname (for localhost dev)
        const tenantConfig = getTenantForHostname();
        if (!tenantConfig || !tenantConfig.userPoolId) {
          // No tenant config available — wait for user to enter email
          setIsLoading(false);
          return;
        }

        const userPool = createUserPool(tenantConfig);
        userPoolRef.current = userPool;
        const currentUser = userPool.getCurrentUser();

        if (!currentUser) {
          setIsLoading(false);
          return;
        }

        currentUser.getSession((err, session) => {
          if (err || !session || !session.isValid()) {
            setIsLoading(false);
            return;
          }
          cognitoUserRef.current = currentUser;
          hydrateSession(currentUser, session);
          setIsLoading(false);
        });
      } catch {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, [hydrateSession]);

  /**
   * Resolve the tenant config for an email address.
   * Returns the config object or null if the domain is not recognised.
   */
  const resolveEmailDomain = useCallback((email) => {
    return getTenantForEmail(email);
  }, []);

  /**
   * Resolve a tenant and create CognitoUser + UserPool refs for an email.
   * Shared by signIn and forgotPassword so both can operate from just an email.
   */
  const resolveAndCreateCognitoUser = useCallback((email) => {
    const tenantConfig = getTenantForEmail(email);
    if (!tenantConfig || !tenantConfig.userPoolId) {
      return null;
    }
    const userPool = createUserPool(tenantConfig);
    userPoolRef.current = userPool;
    const cognitoUser = createCognitoUser(email, userPool);
    cognitoUserRef.current = cognitoUser;
    return { userPool, cognitoUser, tenantConfig };
  }, []);

  /**
   * Sign in with email + password using Cognito SRP.
   */
  const signIn = useCallback((email, password) => {
    return new Promise((resolve, reject) => {
      setError(null);

      const resolved = resolveAndCreateCognitoUser(email);
      if (!resolved) {
        const err = new Error('DOMAIN_NOT_RECOGNIZED');
        setError(err.message);
        return reject(err);
      }

      const { cognitoUser } = resolved;
      const authDetails = createAuthDetails(email, password);

      cognitoUser.authenticateUser(authDetails, {
        onSuccess(session) {
          hydrateSession(cognitoUser, session);
          setAuthChallenge(null);
          resolve({ success: true });
        },

        onFailure(err) {
          const mapped = mapCognitoError(err);
          setError(mapped.message);
          if (mapped.redirectToForgotPassword) {
            setAuthChallenge(AUTH_CHALLENGE.PASSWORD_RESET_REQUIRED);
            resolve({ success: false, challenge: AUTH_CHALLENGE.PASSWORD_RESET_REQUIRED });
          } else {
            reject(err);
          }
        },

        newPasswordRequired(_userAttributes, _requiredAttributes) {
          setAuthChallenge(AUTH_CHALLENGE.NEW_PASSWORD_REQUIRED);
          resolve({ success: false, challenge: AUTH_CHALLENGE.NEW_PASSWORD_REQUIRED });
        },

        // MFA is not supported in the current UI. Keep defensive callbacks to
        // prevent silent hangs for users who previously enrolled in TOTP.
        totpRequired() {
          setError('MFA is not supported in this version. Contact your administrator to disable MFA on your account.');
          reject(new Error('MFA_NOT_SUPPORTED'));
        },

        mfaRequired() {
          setError('MFA is not supported in this version. Contact your administrator to disable MFA on your account.');
          reject(new Error('MFA_NOT_SUPPORTED'));
        },
      });
    });
  }, [hydrateSession, resolveAndCreateCognitoUser]);

  /**
   * Complete the NEW_PASSWORD_REQUIRED challenge (first-time login).
   */
  const completeNewPassword = useCallback((newPassword) => {
    return new Promise((resolve, reject) => {
      if (!cognitoUserRef.current) {
        return reject(new Error('No active sign-in session.'));
      }
      setError(null);

      cognitoUserRef.current.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess(session) {
          hydrateSession(cognitoUserRef.current, session);
          setAuthChallenge(null);
          resolve({ success: true });
        },
        onFailure(err) {
          const mapped = mapCognitoError(err);
          setError(mapped.message);
          reject(err);
        },
      });
    });
  }, [hydrateSession]);

  /**
   * Initiate forgot-password flow — sends a verification code to the user's email.
   *
   * Note: The SDK calls `inputVerificationCode` when the code is sent (not `onSuccess`).
   * `onSuccess` fires only after the entire reset flow completes via confirmPassword.
   */
  const forgotPassword = useCallback((email) => {
    return new Promise((resolve, reject) => {
      setError(null);

      const resolved = resolveAndCreateCognitoUser(email);
      if (!resolved) {
        const err = new Error('DOMAIN_NOT_RECOGNIZED');
        setError(err.message);
        return reject(err);
      }

      const { cognitoUser } = resolved;

      cognitoUser.forgotPassword({
        onSuccess(data) {
          resolve({ success: true, destination: data });
        },
        onFailure(err) {
          const mapped = mapCognitoError(err);
          setError(mapped.message);
          reject(err);
        },
        inputVerificationCode(data) {
          resolve({ success: true, codeDeliveryDetails: data });
        },
      });
    });
  }, [resolveAndCreateCognitoUser]);

  /**
   * Confirm a password reset with the verification code and new password.
   * Must be called after forgotPassword() has sent the code.
   */
  const confirmPasswordReset = useCallback((verificationCode, newPassword) => {
    return new Promise((resolve, reject) => {
      if (!cognitoUserRef.current) {
        return reject(new Error('No active password reset session.'));
      }
      setError(null);

      cognitoUserRef.current.confirmPassword(verificationCode, newPassword, {
        onSuccess() {
          resolve({ success: true });
        },
        onFailure(err) {
          const mapped = mapCognitoError(err);
          setError(mapped.message);
          reject(err);
        },
      });
    });
  }, []);

  /**
   * Change password for an authenticated user.
   * Validates that the current session is still valid before calling the SDK.
   */
  const changePassword = useCallback((oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      if (!cognitoUserRef.current) {
        return reject(new Error('No active session. Please sign in again.'));
      }
      setError(null);

      // Ensure session is current before calling changePassword
      cognitoUserRef.current.getSession((sessionErr, session) => {
        if (sessionErr || !session || !session.isValid()) {
          return reject(new Error('Session expired. Please sign in again.'));
        }

        cognitoUserRef.current.changePassword(oldPassword, newPassword, (err) => {
          if (err) {
            const mapped = mapCognitoError(err);
            setError(mapped.message);
            return reject(err);
          }
          resolve({ success: true });
        });
      });
    });
  }, []);

  /**
   * Sign out — clears Cognito session and resets all auth state.
   */
  const signOut = useCallback(() => {
    if (cognitoUserRef.current) {
      cognitoUserRef.current.signOut();
    } else if (userPoolRef.current) {
      const currentUser = userPoolRef.current.getCurrentUser();
      if (currentUser) currentUser.signOut();
    }
    cognitoUserRef.current = null;
    setUser(null);
    setAccessToken(null);
    setIsAuthenticated(false);
    setAuthChallenge(null);
    setError(null);
  }, []);

  // Stable callback for API clients — returns the current in-memory access token.
  // apiFetch and useSelfHelpChat call this before each request.
  const getToken = useCallback(() => accessToken, [accessToken]);

  const value = {
    user,
    accessToken,
    getToken,
    isAuthenticated,
    isLoading,
    authChallenge,
    error,
    resolveEmailDomain,
    signIn,
    completeNewPassword,
    forgotPassword,
    confirmPasswordReset,
    changePassword,
    signOut,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth — consume the auth context in any component.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
